import React, { useState, useEffect, useCallback } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import { Subtitle } from '@tremor/react';
import moment from 'moment';
import Select from 'react-select';
import { Spacing } from '_styles';
import { EmployeeDropdown } from '_components/molecules/dropdowns';
import { getFlightsByDateTime } from '_services/flight-audit-report-service';
import { getFlightTasks } from '_services/timeline-service';

const MAX_QUERY_HOURS = 24;
const TASK_TIME_OFFSET_MS = 20 * 60 * 1000; // 20 min before/after task times for geo/beacon query

/** Extract timestamp in ms from Firestore Timestamp (seconds/nanoseconds or _seconds/_nanoseconds) or ISO string */
const toMillis = (ts) => {
  if (!ts) return null;
  if (typeof ts === 'object') {
    const seconds = ts.seconds ?? ts._seconds;
    const nanoseconds = ts.nanoseconds ?? ts._nanoseconds ?? 0;
    if (typeof seconds === 'number') {
      return seconds * 1000 + (nanoseconds / 1e6);
    }
  }
  const d = new Date(ts);
  return Number.isFinite(d.getTime()) ? d.getTime() : null;
};

const MAX_QUERY_HOURS_SIMPLE = 2;

const FlightAuditFilters = ({
  iata,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  selectedFlight,
  setSelectedFlight,
  selectedEmployee,
  setSelectedEmployee,
  onSearch,
  isLoading,
  hideFlightDropdown = false,
  runReportRef = null,
  onCanRunChange = null,
}) => {
  const [selectedType, setSelectedType] = useState('all');
  const [flightsList, setFlightsList] = useState([]);
  const [flightsLoading, setFlightsLoading] = useState(false);
  const [flightsError, setFlightsError] = useState(null);
  const [flightTasks, setFlightTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksError, setTasksError] = useState(null);

  const taskStartTime = selectedEmployee?.tasks?.length
    ? (() => {
        let min = null;
        for (const t of selectedEmployee.tasks) {
          const candidates = [
            t.startTime,
            t.allocatedTime,
            t.acknowledgedTime,
          ].map(toMillis).filter(Boolean);
          const m = candidates.length ? Math.min(...candidates) : null;
          if (m != null && (min == null || m < min)) min = m;
        }
        return min;
      })()
    : null;

  const taskEndTime = selectedEmployee?.tasks?.length
    ? (() => {
        let max = null;
        for (const t of selectedEmployee.tasks) {
          const candidates = [
            t.stopTime,
            t.completeTime,
          ].map(toMillis).filter(Boolean);
          const m = candidates.length ? Math.max(...candidates) : null;
          if (m != null && (max == null || m > max)) max = m;
        }
        return max;
      })()
    : null;

  const fetchFlights = useCallback(async () => {
    if (!iata || !startDate || !endDate) {
      setFlightsList([]);
      return;
    }
    setFlightsLoading(true);
    setFlightsError(null);
    try {
      const res = await getFlightsByDateTime({
        iata,
        startDateTime: new Date(startDate).toISOString(),
        endDateTime: new Date(endDate).toISOString(),
        type: selectedType === 'all' ? undefined : selectedType,
      });
      setFlightsList(res?.flights || []);
    } catch (e) {
      setFlightsError(e.message || 'Failed to load flights');
      setFlightsList([]);
    } finally {
      setFlightsLoading(false);
    }
  }, [iata, startDate, endDate, selectedType]);

  const fetchFlightTasks = useCallback(async () => {
    const flightDate = selectedFlight?.flightDate || selectedFlight?.date;
    if (!iata || !selectedFlight?.flightNumber || !flightDate) {
      setFlightTasks([]);
      setSelectedEmployee(null);
      return;
    }
    const dateStr = typeof flightDate === 'string' ? flightDate.split('T')[0] : moment(flightDate).format('YYYY-MM-DD');
    setTasksLoading(true);
    setTasksError(null);
    setSelectedEmployee(null);
    try {
      const tasks = await getFlightTasks({
        iata,
        flightNumber: selectedFlight.flightNumber,
        date: dateStr,
      });
      setFlightTasks(Array.isArray(tasks) ? tasks : []);
    } catch (e) {
      setTasksError(e.message || 'Failed to load employees');
      setFlightTasks([]);
    } finally {
      setTasksLoading(false);
    }
  }, [iata, selectedFlight?.flightNumber, selectedFlight?.flightDate, selectedFlight?.date]);

  useEffect(() => {
    fetchFlights();
  }, [fetchFlights]);

  useEffect(() => {
    fetchFlightTasks();
  }, [fetchFlightTasks]);

  const handleStartDateChange = (value) => {
    setStartDate(value);
    const end = moment(value).add(MAX_QUERY_HOURS, 'hours');
    if (moment(endDate).isAfter(end)) {
      setEndDate(end.format('YYYY-MM-DDTHH:mm'));
    }
  };

  const handleEndDateChange = (value) => {
    setEndDate(value);
    const start = moment(value).subtract(MAX_QUERY_HOURS, 'hours');
    if (moment(startDate).isBefore(start)) {
      setStartDate(start.format('YYYY-MM-DDTHH:mm'));
    }
  };

  const uniqueEmployees = React.useMemo(() => {
    const byId = new Map();
    for (const t of flightTasks) {
      const id = t?.employeeId;
      if (!id) continue;
      if (!byId.has(id)) {
        byId.set(id, {
          employeeId: id,
          employeeName: t?.employeeName || id,
          tasks: [],
        });
      }
      byId.get(id).tasks.push(t);
    }
    return Array.from(byId.values());
  }, [flightTasks]);

  const flightOptions = flightsList.map((f) => ({
    value: f._id,
    label: `${f.flightNumber} - ${f.flightDate || '-'} - ${f.departureAirport}→${f.arrivalAirport}`,
    ...f,
  }));

  const employeeOptions = uniqueEmployees.map((e) => ({
    value: e.employeeId,
    label: `${e.employeeName} (${e.employeeId})`,
    ...e,
  }));

  const selectedFlightOption = selectedFlight
    ? flightOptions.find((o) =>
        o.value === selectedFlight.value ||
        o._id === selectedFlight._id ||
        (o.flightNumber === selectedFlight.flightNumber && (o.flightDate === selectedFlight.flightDate || o.date === selectedFlight.flightDate))
      ) || { value: selectedFlight._id, label: `${selectedFlight.flightNumber} - ${selectedFlight.flightDate || '-'} - ${selectedFlight.departureAirport}→${selectedFlight.arrivalAirport}` }
    : null;

  const selectedEmployeeOption = selectedEmployee
    ? employeeOptions.find((o) => o.employeeId === selectedEmployee.employeeId)
    : null;

  const handleFlightSelect = (opt) => {
    setSelectedFlight(opt ? { ...opt } : null);
  };

  const handleEmployeeSelect = (opt) => {
    setSelectedEmployee(opt ? { employeeId: opt.employeeId, employeeName: opt.employeeName, tasks: opt.tasks } : null);
  };

  const handleSearch = () => {
    if (!selectedEmployee?.employeeId || !iata) return;
    const start = taskStartTime != null
      ? new Date(taskStartTime - TASK_TIME_OFFSET_MS).toISOString()
      : new Date(startDate).toISOString();
    let end = taskEndTime != null
      ? new Date(taskEndTime + TASK_TIME_OFFSET_MS).toISOString()
      : null;
    if (!end && taskStartTime != null) {
      end = new Date(taskStartTime + 2 * 60 * 60 * 1000).toISOString();
    }
    if (!end) end = new Date(endDate).toISOString();
    onSearch({
      employeeId: selectedEmployee.employeeId,
      startDate: start,
      endDate: end,
      selectedEmployeeTasks: selectedEmployee.tasks,
    });
  };

  const canSearch = !!selectedEmployee?.employeeId && !!iata;

  useEffect(() => {
    if (onCanRunChange && !hideFlightDropdown) onCanRunChange(canSearch);
  }, [onCanRunChange, hideFlightDropdown, canSearch]);

  // Wire Run Report button (from parent) to trigger search when runReportRef provided
  useEffect(() => {
    if (runReportRef && !hideFlightDropdown) {
      runReportRef.current = () => {
        if (!canSearch) return;
        handleSearch();
      };
      return () => { runReportRef.current = null; };
    }
  }, [runReportRef, hideFlightDropdown, canSearch, selectedEmployee, taskStartTime, taskEndTime, startDate, endDate, onSearch]);

  // Simple mode: date range + employee only (for employee location audit report)
  const handleSimpleSearch = React.useCallback(() => {
    if (!selectedEmployee?.employeeId || !iata) return;
    onSearch({
      employeeId: selectedEmployee.employeeId,
      startDate: new Date(startDate).toISOString(),
      endDate: new Date(endDate).toISOString(),
      selectedEmployeeTasks: [],
    });
  }, [selectedEmployee?.employeeId, iata, startDate, endDate, onSearch]);

  const canRunSimple = !!selectedEmployee?.employeeId && !!iata;
  useEffect(() => {
    if (onCanRunChange && hideFlightDropdown) onCanRunChange(canRunSimple);
  }, [onCanRunChange, hideFlightDropdown, canRunSimple]);

  useEffect(() => {
    if (runReportRef && hideFlightDropdown) {
      runReportRef.current = () => {
        if (!canRunSimple) return;
        handleSimpleSearch();
      };
      return () => { runReportRef.current = null; };
    }
  }, [runReportRef, hideFlightDropdown, canRunSimple, handleSimpleSearch]);

  if (hideFlightDropdown) {
    const handleStartChange = (v) => {
      setStartDate(v);
      const end = moment(v).add(MAX_QUERY_HOURS_SIMPLE, 'hours');
      if (moment(endDate).isAfter(end)) setEndDate(end.format('YYYY-MM-DDTHH:mm'));
    };
    const handleEndChange = (v) => {
      setEndDate(v);
      const start = moment(v).subtract(MAX_QUERY_HOURS_SIMPLE, 'hours');
      if (moment(startDate).isBefore(start)) setStartDate(start.format('YYYY-MM-DDTHH:mm'));
    };
    return (
      <div style={{ marginTop: Spacing.SCALE_22 }}>
        <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: 8, marginTop: 0 }}>
          Set the date/time range to query flights within.
        </p>
        <Row style={{ marginBottom: Spacing.SCALE_22 }}>
          <Col xs={12} md={6}>
            <Subtitle style={{ fontSize: '14px' }}>Start Range</Subtitle>
            <Form.Control
              type="datetime-local"
              value={startDate}
              onChange={(e) => handleStartChange(e.target.value)}
              max={endDate}
              style={{ fontSize: '16px' }}
            />
          </Col>
          <Col xs={12} md={6}>
            <Subtitle style={{ fontSize: '14px' }}>End Range</Subtitle>
            <Form.Control
              type="datetime-local"
              value={endDate}
              onChange={(e) => handleEndChange(e.target.value)}
              min={startDate}
              style={{ fontSize: '16px' }}
            />
          </Col>
        </Row>
        <Row style={{ marginBottom: Spacing.SCALE_22 }}>
          <Col xs={12} md={6}>
            <EmployeeDropdown
              label="Employee"
              onSelect={(e) => setSelectedEmployee(e)}
              onClear={() => setSelectedEmployee(null)}
            />
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div style={{ marginTop: Spacing.SCALE_22 }}>
      <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: 8, marginTop: 0 }}>
        Set the date/time range to query flights within.
      </p>
      {/* Date range */}
      <Row style={{ marginBottom: Spacing.SCALE_22 }}>
        <Col xs={12} md={6}>
          <Subtitle style={{ fontSize: '14px' }}>Start Range</Subtitle>
          <Form.Control
            type="datetime-local"
            value={startDate}
            onChange={(e) => handleStartDateChange(e.target.value)}
            max={endDate}
            style={{ fontSize: '16px' }}
          />
        </Col>
        <Col xs={12} md={6}>
          <Subtitle style={{ fontSize: '14px' }}>End Range</Subtitle>
          <Form.Control
            type="datetime-local"
            value={endDate}
            onChange={(e) => handleEndDateChange(e.target.value)}
            min={startDate}
            style={{ fontSize: '16px' }}
          />
        </Col>
      </Row>

      {/* Type filter */}
      <Row style={{ marginBottom: Spacing.SCALE_22 }}>
        <Col xs={12} md={6}>
          <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: 6, display: 'block' }}>
            Flight Type
          </label>
          <Form.Select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            style={{ fontSize: '14px' }}
          >
            <option value="all">All</option>
            <option value="inbound">Inbound</option>
            <option value="outbound">Outbound</option>
          </Form.Select>
        </Col>
      </Row>

      {/* Flights dropdown */}
      <Row style={{ marginBottom: Spacing.SCALE_22 }}>
        <Col xs={12} md={6}>
          <label style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>
            Flight
          </label>
          <Select
            placeholder={flightsLoading ? 'Loading flights...' : 'Select a flight'}
            options={flightOptions}
            value={selectedFlightOption}
            onChange={handleFlightSelect}
            isLoading={flightsLoading}
            isClearable
            styles={{
              control: (base) => ({ ...base, minHeight: 38, fontSize: 14 }),
            }}
          />
          {flightsError && <p style={{ color: 'red', fontSize: 12, marginTop: 4 }}>{flightsError}</p>}
        </Col>
      </Row>

      {/* Employees dropdown */}
      <Row style={{ marginBottom: Spacing.SCALE_22 }}>
        <Col xs={12} md={6}>
          <label style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>
            Employee
          </label>
          <Select
            placeholder={!selectedFlight ? 'Select a flight first' : tasksLoading ? 'Loading employees...' : 'Select an employee'}
            options={employeeOptions}
            value={selectedEmployeeOption}
            onChange={handleEmployeeSelect}
            isLoading={tasksLoading}
            isDisabled={!selectedFlight}
            isClearable
            styles={{
              control: (base) => ({ ...base, minHeight: 38, fontSize: 14 }),
            }}
          />
          {tasksError && <p style={{ color: 'red', fontSize: 12, marginTop: 4 }}>{tasksError}</p>}
        </Col>
      </Row>

      {/* Derived start/end time */}
      {selectedEmployee && (taskStartTime != null || taskEndTime != null) && (
        <Row style={{ marginBottom: Spacing.SCALE_22 }}>
          <Col xs={12} md={6}>
            <label style={{ fontSize: '12px', fontWeight: 500, color: '#6b7280', marginBottom: 4, display: 'block' }}>
              Task time range (from selected employee&apos;s tasks)
            </label>
            <p style={{ fontSize: '14px', color: '#374151', margin: 0 }}>
              {taskStartTime != null
                ? moment(taskStartTime).format('YYYY-MM-DD HH:mm')
                : 'N/A'}
              {' → '}
              {taskEndTime != null
                ? moment(taskEndTime).format('YYYY-MM-DD HH:mm')
                : 'N/A'}
            </p>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default FlightAuditFilters;
